import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch } from "react-redux";
import { GraphqlApi } from 'services/api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { snackBar } from "actions/modelActions";
import { ButtonSpinner } from 'components/ButtonSpinner';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import "./ui-specs.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const TABS = [
    { label: 'CONSUMER',   family: 'CONS',  headerName: 'Consumer Choice IDs' },
    { label: 'COMMERCIAL', family: 'COMM',  headerName: 'Commercial Choice IDs' },
    { label: 'PRINT',      family: 'PRINT', headerName: 'Print Choice IDs' },
];

const defaultColDef = {
    flex: 1,
    sortable: false,
    suppressMovable: true,
    headerClass: 'ui-spec-ag-header',
};

const gridStyle = { width: '50%' };

const CustomHeader = ({ displayName, totalRows }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <span>{displayName}</span>
        <span style={{ color: '#888', fontSize: '12px', fontWeight: 'normal' }}>Total Rows: {totalRows}</span>
    </div>
);

function UISpecs() {
    const [rowIds, setRowIds] = useState({ CONS: [], COMM: [], PRINT: [] });
    const [selectedTab, setSelectedTab] = useState(0);
    const [shouldBeDisabled, setShouldBeDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();

    useEffect(() => {
        GraphqlApi.query('choiceSequence')
            .then((res) => {
                const updated = { CONS: [], COMM: [], PRINT: [] };
                res.data.choiceSequence?.forEach(({ family, choiceIDs }) => {
                    if (family in updated) updated[family] = choiceIDs;
                });
                setRowIds(updated);
            })
            .catch((error) => dispatch(snackBar(true, error.message)))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formattedRows = useMemo(() => ({
        CONS:  rowIds.CONS.map(id => ({ id })),
        COMM:  rowIds.COMM.map(id => ({ id })),
        PRINT: rowIds.PRINT.map(id => ({ id })),
    }), [rowIds]);

    const handleRowDragEnd = useCallback((params, family) => {
        const newRows = [];
        params.api.forEachNode(node => newRows.push(node.data));
        setRowIds(prev => ({ ...prev, [family]: newRows.map(r => r.id) }));
        setShouldBeDisabled(false);
    }, []);

    const handleUpdate = () => {
        const model = TABS.map(({ family }) => ({ family, choiceIDs: rowIds[family] }));
        GraphqlApi.query('updateChoiceSequence', { model })
            .then(() => {
                dispatch(snackBar(true, 'Choice sequence updated successfully', 'success'));
                setShouldBeDisabled(true);
            })
            .catch((error) => dispatch(snackBar(true, error.message, 'error')));
    };

    const { family, headerName } = TABS[selectedTab];
    const rows = formattedRows[family];

    const columnDefs = [{
        field: 'id',
        headerName,
        rowDrag: true,
        headerComponent: CustomHeader,
        headerComponentParams: { totalRows: rows.length },
    }];

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '15px', mt: '15px' }}>
                <Typography variant="h5">UI-Specs</Typography>
                <Button
                    variant="outlined"
                    disabled={shouldBeDisabled}
                    sx={{ marginLeft: '35px', borderRadius: '8px' }}
                    onClick={handleUpdate}
                >
                    Update
                </Button>
            </Box>

            <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} aria-label="DataGrid Tabs">
                {TABS.map(({ label }) => <Tab key={label} label={label} />)}
            </Tabs>

            {isLoading ? (
                <ButtonSpinner />
            ) : (
                <div className="ag-theme-alpine ui-specs-grid" style={gridStyle}>
                    <AgGridReact
                        key={family}
                        columnDefs={columnDefs}
                        rowData={rows}
                        getRowId={(params) => String(params.data.id)}
                        rowHeight={38}
                        headerHeight={48}
                        defaultColDef={defaultColDef}
                        domLayout='autoHeight'
                        rowDragManaged={true}
                        rowDragEntireRow={true}
                        animateRows={true}
                        onRowDragEnd={(params) => handleRowDragEnd(params, family)}
                        suppressColumnVirtualisation={true}
                        suppressCellFocus={true}
                    />
                </div>
            )}
        </>
    );
}

export default UISpecs;

